import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Award, MessageCircle, Copy, CheckCheck, UserPlus, BookOpen, PlayCircle, CheckCircle2, CalendarDays, Sparkles } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { Loading, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";

function LineLinkSection() {
  const [info, setInfo] = useState(null);
  const load = () => api.get("/line/link-code").then(({ data }) => setInfo(data)).catch(() => setInfo(false));
  useEffect(() => { load(); }, []);
  if (info === null) return null;
  if (info === false || !info.enabled) return null;

  const copy = () => { navigator.clipboard?.writeText(info.code); toast.success("連携コードをコピーしました"); };

  return (
    <section className="mt-8" data-testid="line-link-section">
      <h2 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-green-600" /> LINEで研修のお知らせを受け取る</h2>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        {info.linked ? (
          <div className="flex items-center gap-2 text-green-700" data-testid="line-linked">
            <CheckCheck className="w-5 h-5" /> LINE連携が完了しています。研修のお知らせがLINEにも届きます。
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-6 items-center">
            <div className="shrink-0 p-3 bg-white rounded-lg border border-slate-100">
              {info.add_friend_url
                ? <QRCodeCanvas value={info.add_friend_url} size={132} data-testid="line-qr" />
                : <div className="w-[132px] h-[132px] flex items-center justify-center text-xs text-slate-400 text-center">友だち追加URL<br />未設定</div>}
            </div>
            <div className="min-w-0 flex-1">
              <ol className="text-sm text-slate-600 space-y-1.5 list-decimal pl-5">
                <li>QRコードを読み取り、LINE公式アカウントを<b>友だち追加</b>します。</li>
                <li>トーク画面で下記の<b>連携コード</b>を送信します。</li>
                <li>「連携が完了しました」と返信が届けば設定完了です。</li>
              </ol>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-slate-400">連携コード</span>
                <code className="font-mono text-lg font-bold tracking-wider bg-slate-100 text-slate-800 px-3 py-1 rounded" data-testid="line-code">{info.code}</code>
                <Button size="sm" variant="outline" onClick={copy} data-testid="line-copy-btn"><Copy className="w-4 h-4" /></Button>
              </div>
              {info.add_friend_url && (
                <a href={info.add_friend_url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm text-green-700 hover:underline" data-testid="line-add-link">スマホの方はこちらから友だち追加</a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function TrainingRow({ entry, cta, navigate }) {
  const t = entry.training || {};
  const status = entry.assignment?.status;
  const done = status === "修了";
  const overdue = status === "期限超過";
  const doing = status === "視聴中" || status === "動画完了";
  const accent = done ? "bg-emerald-400" : overdue ? "bg-rose-500" : doing ? "bg-amber-400" : "bg-blue-500";
  const iconWrap = done ? "bg-emerald-50 text-emerald-600" : overdue ? "bg-rose-50 text-rose-600" : doing ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600";
  const Icon = done ? CheckCircle2 : doing ? PlayCircle : BookOpen;
  return (
    <div className="group relative flex items-center gap-4 pl-5 pr-3 py-4 rounded-2xl border border-slate-200/80 bg-white hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/5 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
      <span className={`absolute left-0 top-0 h-full w-1.5 ${accent}`} />
      <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${iconWrap}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-800 truncate">{t.name}</div>
        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> 受講期限：{entry.assignment?.due_date || "指定なし"}</div>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="hidden sm:block"><StatusBadge status={status} /></div>
        <Button size="sm" data-testid={`learn-btn-${t.id}`}
          className={`rounded-full shadow-sm ${done ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}`}
          onClick={() => navigate(`/learn/${t.id}`)}>
          {cta} <ChevronRight className="w-4 h-4 ml-0.5 group-hover:translate-x-0.5 transition-transform" />
        </Button>
      </div>
    </div>
  );
}

function StaffOnbCard({ navigate }) {
  const [onb, setOnb] = useState(null);
  useEffect(() => { api.get("/onboarding/my").then(({ data }) => setOnb(data)).catch(() => {}); }, []);
  if (!onb || !onb.onboarding) return null;
  const eo = onb.onboarding;
  return (
    <button onClick={() => navigate("/onboarding")} data-testid="staff-onboarding-card"
      className="w-full text-left rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 mb-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-blue-700 font-semibold"><UserPlus className="w-5 h-5" />入社研修</div>
        <span className="text-xs bg-white px-2 py-0.5 rounded-full border border-blue-100 text-blue-700">{eo.status}</span>
      </div>
      <div className="mt-2 flex items-end justify-between">
        <div className="text-2xl font-bold text-slate-800">{eo.done_items || 0} / {eo.total_items || 0} 修了</div>
        <div className="text-right"><div className="text-xl font-bold text-blue-600">{eo.progress || 0}%</div><div className="text-[11px] text-slate-500">期限 {eo.due_date || "-"}</div></div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${eo.progress || 0}%` }} /></div>
    </button>
  );
}

const FY_MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

function ctaFor(status) {
  if (status === "修了") return "確認する";
  if (status === "未開始") return "受講する";
  return "続きから";
}

export default function StaffHome() {
  const { user } = useAuth();
  const { fiscalYear } = useFiscalYear();
  const [data, setData] = useState(null);
  const [selMonth, setSelMonth] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!fiscalYear) return;
    api.get(`/dashboard?fiscal_year=${fiscalYear}`).then(({ data }) => setData(data));
  }, [fiscalYear]);

  // Build month -> entries map from all statuses
  const byMonth = {};
  if (data) {
    const all = [...(data.todo || []), ...(data.doing || []), ...(data.done || [])];
    for (const e of all) {
      const key = e.month != null ? e.month : "other";
      (byMonth[key] = byMonth[key] || []).push(e);
    }
  }

  // Ordered list of month tabs that actually have trainings
  const tabs = [];
  for (const m of FY_MONTHS) if (byMonth[m]?.length) tabs.push(m);
  if (byMonth["other"]?.length) tabs.push("other");

  // Default selected month: current month if present, else first tab
  useEffect(() => {
    if (!data || selMonth !== null) return;
    const nowM = new Date().getMonth() + 1;
    const avail = [];
    for (const m of FY_MONTHS) if (byMonth[m]?.length) avail.push(m);
    if (byMonth["other"]?.length) avail.push("other");
    if (!avail.length) { setSelMonth("none"); return; }
    setSelMonth(avail.includes(nowM) ? nowM : avail[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data) return <Loading />;

  const todoCount = (m) => (byMonth[m] || []).filter((e) => e.assignment?.status === "未開始" || e.assignment?.status === "期限超過").length;
  const monthLabel = (m) => (m === "other" ? "その他" : `${m}月`);
  const current = selMonth != null && selMonth !== "none" ? (byMonth[selMonth] || []) : [];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="relative mb-6 overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 px-6 py-6 sm:px-8 sm:py-7 text-white shadow-lg shadow-blue-600/20 wcc-fadein">
        <div className="absolute -right-8 -top-10 w-44 h-44 rounded-full bg-white/10" />
        <div className="absolute right-20 bottom-0 w-24 h-24 rounded-full bg-white/5" />
        <div className="relative flex items-center gap-4">
          <div className="shrink-0 w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center text-xl font-bold border border-white/20">
            {(user.name || "").trim().charAt(0) || "?"}
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">こんにちは、{user.name} さん</h1>
            <p className="text-sm text-blue-100 mt-0.5 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> {fiscalYear}年度の研修状況です</p>
          </div>
        </div>
        <div className="relative mt-5 flex gap-3">
          <div className="flex-1 rounded-2xl bg-white/10 backdrop-blur px-4 py-3 border border-white/15">
            <div className="text-2xl font-bold" data-testid="summary-open">{(data.todo?.length || 0) + (data.doing?.length || 0)}</div>
            <div className="text-[11px] text-blue-100 mt-0.5">未受講・受講中</div>
          </div>
          <div className="flex-1 rounded-2xl bg-white/10 backdrop-blur px-4 py-3 border border-white/15">
            <div className="text-2xl font-bold" data-testid="summary-done">{data.done?.length || 0}</div>
            <div className="text-[11px] text-blue-100 mt-0.5">修了</div>
          </div>
        </div>
      </div>

      <StaffOnbCard navigate={navigate} />

      {tabs.length > 0 ? (
        <>
          <div className="flex flex-wrap justify-center gap-2 pt-3 pb-3 mb-4 -mx-1 px-1" data-testid="month-tabs">
            {tabs.map((m) => {
              const n = todoCount(m);
              const active = selMonth === m;
              return (
                <button
                  key={m}
                  data-testid={`month-tab-${m}`}
                  onClick={() => setSelMonth(m)}
                  className={`relative shrink-0 px-4 py-2.5 rounded-2xl text-sm font-bold transition-all duration-200 ${
                    active
                      ? "bg-gradient-to-b from-blue-600 to-blue-700 text-white shadow-md shadow-blue-600/30 scale-105"
                      : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-700"
                  } ${!active && n > 0 ? "wcc-blink-tab" : ""}`}
                >
                  {monthLabel(m)}
                  {n > 0 && (
                    <span data-testid={`month-badge-${m}`}
                      className="wcc-blink-badge absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold flex items-center justify-center border border-white shadow">
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <section className="wcc-fadein" data-testid="month-trainings">
            <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-lg">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-700"><CalendarDays className="w-4 h-4" /></span>
              {monthLabel(selMonth)}の研修 <span className="text-sm font-medium text-slate-400">（{current.length}件）</span>
            </h2>
            <div className="space-y-3">
              {current.map((e) => (
                <TrainingRow key={e.assignment.id} entry={e} cta={ctaFor(e.assignment?.status)} navigate={navigate} />
              ))}
            </div>
            {(data.done?.length > 0) && (
              <Button variant="outline" className="mt-5 rounded-full border-slate-300 hover:border-amber-300 hover:bg-amber-50" data-testid="goto-certificates" onClick={() => navigate("/certificates")}>
                <Award className="w-4 h-4 mr-2 text-amber-500" /> 修了証を確認する
              </Button>
            )}
          </section>
        </>
      ) : (
        <div className="text-center py-16 text-slate-400" data-testid="no-trainings">割り当てられた研修はありません</div>
      )}

      <LineLinkSection />
    </div>
  );
}
